import { Alert, AlertIcon, Badge, Button, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { MatchDetailsWithExclusion } from '../../api/get-match-details'
import { matchDetailsQuery } from '../../api/get-match-details'
import type { MatchExclusion } from '../../api/update-match-exclusion'
import { updateMatchExclusion } from '../../api/update-match-exclusion'
import { HasMCAccess, MCRequiredLevelEnum } from '../../helpers/auth'

export function FixtureExclusionControl({ matchId, exclusion }: { matchId: string; exclusion: MatchExclusion }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (excluded: boolean) => updateMatchExclusion(matchId, excluded),
    onSuccess: async result => {
      queryClient.setQueryData<MatchDetailsWithExclusion>(matchDetailsQuery(matchId).queryKey, previous =>
        previous ? { ...previous, exclusion: result.exclusion } : previous
      )
      await queryClient.invalidateQueries({ queryKey: ['match-centre-matches'] })
    },
  })

  const action = exclusion.operatorExcluded
    ? exclusion.sourceInvalid
      ? 'Clear manual exclusion'
      : 'Restore to Scout'
    : exclusion.sourceInvalid
    ? 'Keep excluded'
    : 'Exclude from Scout'

  return (
    <Stack alignItems="flex-start" spacing={3}>
      <Badge colorScheme={exclusion.excludedFromScout ? 'orange' : 'green'}>
        {exclusion.excludedFromScout ? 'Excluded from Scout' : 'Included in Scout'}
      </Badge>
      <Text>
        Excluded fixtures are hidden from Scout and Match Centre lists. Their dates, scores and squads are retained.
      </Text>
      {exclusion.sourceInvalid && (
        <Alert status="warning">
          <AlertIcon />
          SDS marked this fixture as incorrect. It stays excluded until SDS corrects it and a refresh succeeds.
        </Alert>
      )}
      {exclusion.operatorExcluded && (
        <Text>A manual exclusion is active. SDS refreshes will preserve it until you clear it here.</Text>
      )}
      {mutation.isError && (
        <Alert status="error">
          <AlertIcon />
          The exclusion could not be updated. Refresh the match details before trying again.
        </Alert>
      )}
      <HasMCAccess minLevel={MCRequiredLevelEnum['clsp-superadmin']}>
        <Button
          variant="secondary"
          isLoading={mutation.isLoading}
          onClick={() => mutation.mutate(!exclusion.operatorExcluded)}
        >
          {action}
        </Button>
      </HasMCAccess>
    </Stack>
  )
}
